<template>
  <div class="bg-gray-50 rounded-lg overflow-hidden">
    <!-- Header with close button -->
    <div class="tw-flex items-center justify-between px-10 py-5 bg-white border-b border-gray-200">
      <h2 class="text-xl font-semibold m-0">
        {{ reportLabel }}
      </h2>
      <DsButton
        @click="$emit('close')"
        variant="rounded-secondary"
        icon="remove"
        :label="$t('create_resource_close')"
        class="ml-auto"
      />
    </div>

    <!-- Reports content -->
    <div>
      <!-- Show specific selected report -->
      <div v-if="selectedReportTemplate">
        <!-- Report call countdown for selected report -->
        <div
          v-if="isProduct && actor.current_report && selectedReportTemplate.is_call && !isOpenCallEmptyAndExpired(actor.current_report, selectedReportTemplate)"
          class="tw-flex items-center gap-2 mb-5 text-black"
          style="background-color: var(--primary-extra-lightest)"
        >
          <span class="font-bold text-blue-600" v-html="callTimeDifference"></span>
          <span class="text-gray-600">left to contribute to {{ selectedReportTemplate.name }}!</span>
          <DsButton
            @click="handleContributeToReport"
            :disabled="isOpenCallEmptyAndExpired(actor.current_report, selectedReportTemplate)"
            :label="`Contribute to ${selectedReportTemplate.name}`"
            class="ml-auto"
            size="small"
          />
        </div>

        <!-- Selected Report Display -->
        <div
          class="p-10 text-black bg-white"
        >
          <ActorDetailReport
            :actor="actor"
            :use-header-title="true"
            :enable-read-more="true"
            :show-back-button="false"
          />
        </div>
      </div>

      <!-- Fallback for no specific report selected -->
      <div v-else class="text-center py-10">
        <h3 class="text-gray-600 mb-2">Select a report</h3>
        <p class="text-gray-500">Please select a specific report to view its details.</p>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import { mapGetters } from 'vuex'
  import ActorDetailReport from './ActorDetail/ActorDetailReport.vue'
  import Container from '../components/Container/Container.vue'
  import ReportMixin from '../util/ReportMixin.js'
  import SimplifiedImageCard from '@/components/Connection/SimplifiedImageCard.vue'

  export default defineComponent({
    name: 'ActorReports',
    components: {
      SimplifiedImageCard,
      ActorDetailReport,
      Container,
    },
    mixins: [ReportMixin],
    props: {
      actor: {
        type: Object,
        required: true,
      },
      selectedReportTemplate: {
        type: Object,
        default: null,
      },
    },
    emits: ['close'],
    computed: {
      ...mapGetters([
        'userCanEdit',
      ]),
      isProduct () {
        return this.actor?.actor_type === 'Product'
      },
      hasReportTemplate () {
        return this.actor?.reports && this.availableReportTemplatesForCurrentActor.length > 0
      },
      companyReportTemplates () {
        if (!this.actor?.reports) return []
        return this.availableReportTemplatesForCurrentActor
      },
      hasFirstSelectedReport () {
        return !!(this.actor?.current_report && this.actor.current_report.length)
      },
      computedSelectedReportTemplateForHeadline () {
        if (!this.hasFirstSelectedReport) return null
        if (!this.availableReportTemplatesForCurrentActor || !this.availableReportTemplatesForCurrentActor.length) return null
        return this.availableReportTemplatesForCurrentActor.find(reportTemplate => {
          return !!this.actor.current_report.find(reportField => {
            return !!reportTemplate.reportFields.find(reportTemplateField =>
              reportTemplateField.id === reportField.ecosystem_report_field_id
            )
          })
        })
      },
      availableReportTemplatesForCurrentActor () {
        if (!this.reportSettings || !this.reportSettings.length) {
          return []
        }
        return this.reportSettings
      },
      userCanAccessReport () {
        // Allow access if user can edit or if there are available reports
        return this.userCanEdit || (this.actor && this.actor.reports && this.actor.reports.length > 0)
      },
      reportLabel () {
        return this.selectedReportTemplate?.name || this.computedSelectedReportTemplateForHeadline?.name || 'Reports'
      },
      callTimeDifference () {
        // This would need to be implemented based on your existing logic
        return ''
      },
    },
    methods: {
      selectReportTemplate (reportTemplate) {
        // Implement report template selection logic
        console.log('Selected report template:', reportTemplate)
      },
      handleContributeToReport () {
        // Implement contribute to report logic
        console.log('Contributing to report')
      },
      isOpenCallEmptyAndExpired (currentReport, reportTemplate) {
        // Implement the logic from your existing codebase
        return false
      },
      getOpenCallStatus (actor, reportTemplate) {
        // Implement the logic from your existing codebase
        return ''
      },
    },
  })
</script>

